import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Alert, Button, Modal, ModalHeader } from '@/components/ui';

/**
 * Deleting a member.
 *
 * The API refuses once there is any lending history, so this is for
 * registrations that were a mistake, duplicates, or test accounts. The error it
 * returns names the specific records in the way, so it is surfaced verbatim
 * rather than replaced with something generic.
 *
 * Shared by the members list and the member file, so the two cannot drift into
 * warning about different things.
 */
export function DeleteMemberModal({
  memberId,
  name,
  onClose,
  onDeleted,
}: {
  memberId: string;
  name: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => api.delete(`/members/${memberId}`),
    onSuccess: () => {
      toast.success(`${name} deleted.`);
      onDeleted();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not delete that member.'),
  });

  return (
    <Modal open onClose={onClose} size="sm">
      <ModalHeader title="Delete this member?" description={name} onClose={onClose} />

      <div className="space-y-4 p-6">
        {error && <Alert tone="danger">{error}</Alert>}
        <p className="text-sm leading-relaxed text-foreground-secondary">
          This removes the account, the member record and their KYC file for good. It cannot be
          undone.
        </p>
        <Alert tone="info">
          A member who has borrowed cannot be deleted — suspend them instead. The audit trail keeps
          the record of this deletion either way.
        </Alert>
      </div>

      <div className="flex justify-end gap-2 border-t border-border p-4">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
          <Trash2 size={15} /> Delete permanently
        </Button>
      </div>
    </Modal>
  );
}
